import type { AppProps as NextAppProps } from 'next/app';
import { SiteContextProvider } from '3-foundation/Site/SiteContext';
import { DictionaryContextProvider } from '3-foundation/Dictionary/DictionaryContext';
import { PageContextProvider } from '3-foundation/PageContent/PageContext';
import { NavigationContextProvider } from '3-foundation/Navigation/NavigationContext';
import 'ui/styles/globals.scss';
import { StrictMode, useEffect, useRef } from 'react';
import Application from '3-foundation/Application/ApplicationEntity';
import { setBrowserSiteContext } from '3-foundation/Logging/siteContext';
import { ApplicationContextProvider } from '3-foundation/Application/ApplicationContext';

// modified version - allows for custom pageProps type, falling back to 'any'
// https://stackoverflow.com/questions/64722812/what-typescript-type-should-nextjs-app-tsx-component-and-pageprops-be
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppProps<P = any> = {
	pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

const App = ({ Component, pageProps }: AppProps) => {
	const { content, dictionary, navigation, site, application } = pageProps ?? {};

	/**
	 * This useEffect transfers application values from pageProps to
	 * a client-side exposed instance of the Application entity that can be
	 * accessed in connectors, repositories and other non React client-side modules
	 *
	 * If you want to access the values inside a React module you can use the
	 * ApplicationContext
	 */

	const isMounted = useRef(false);

	if (!isMounted.current) {
		// The code here, executes before render - on mount
		if (application?.envVars) Application.envVars = application.envVars;
		setBrowserSiteContext({ siteHost: site?.host ?? (typeof window !== 'undefined' ? window.location.hostname : undefined) });
	}

	useEffect(() => {
		// Initial render has been completed, set isMounted true
		isMounted.current = true;
	}, []);

	// useEffect(() => {
	// 	// Values that change between renders must be updated continuously
	// });

	return (
		<StrictMode>
			<ApplicationContextProvider application={application}>
				<SiteContextProvider site={site}>
					<DictionaryContextProvider dictionary={dictionary ?? []}>
						<PageContextProvider content={content}>
							<NavigationContextProvider navigation={navigation}>
								<Component {...pageProps} />
							</NavigationContextProvider>
						</PageContextProvider>
					</DictionaryContextProvider>
				</SiteContextProvider>
			</ApplicationContextProvider>
		</StrictMode>
	);
};

export default App;
